import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  ButtonIconWithLabel,
  Icon_Checkmark24,
  Icon_Disconnect24,
  Icon_People,
  Icon_People_Slash,
  Modal,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

import useAccount from "../../../../hooks/useAccount";
import { useProjectSharing } from "../../../../hooks/useProjectSharing";
import {
  type ProjectViewLinkInvitation,
  type ProjectViewLinkMember,
  useProjectViewLinkSharing,
} from "../../../../hooks/useProjectViewLink";
import {
  type WebExportBaseRole,
  WebExportBaseRoles,
  type WebExportSharedState,
  WebExportSharedStates,
} from "../../../../hooks/useWebExport";
import { SearchParam, SearchParamIntent } from "../../../../types";
import useTracker from "../../../../utils/useTracker";
import { Avatar } from "../../../Comment/Avatar";
import { EmailInvitationFormProject } from "./components/EmailInvitationFormProject";
import { InvitationRowProject } from "./components/InvitationRowProject";
import { MemberRowProject } from "./components/MemberRowProject";
import { OverlayProvider } from "./components/MenuPortal";
import { Separator } from "./components/Separator";

import style from "./ShareProjectModal.module.css";

const schema = z.object({
  generalAccess: z.union([
    z.literal(WebExportSharedStates.AnyoneWithLink),
    z.literal(WebExportSharedStates.TeamOnly),
    z.literal(WebExportSharedStates.Restricted),
  ]),
  baseRole: z.union([
    z.literal(WebExportBaseRoles.Viewer),
    z.literal(WebExportBaseRoles.Commenter),
  ]),
});
type ShareModalFormData = z.infer<typeof schema>;

export const ShareProjectModal: React.FC = () => {
  const tracker = useTracker();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareModalOpen, setShareModalOpen] = useState(
    searchParams.get(SearchParam.Intent) === SearchParamIntent.Share
  );
  const [copySuccessVisible, setCopySuccessVisible] = useState(false);

  const { data: projectViewLinkSharingData } = useProjectViewLinkSharing();

  const consumeShareIntent = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        searchParams.delete(SearchParam.Intent);
        return searchParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const { setValue, reset, clearErrors } = useForm<ShareModalFormData>({
    mode: "onChange",
    defaultValues: {
      generalAccess: projectViewLinkSharingData?.sharedState as Exclude<
        WebExportSharedState,
        "not_shared"
      >,
      baseRole: projectViewLinkSharingData?.baseRole as WebExportBaseRole,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    setValue(
      "generalAccess",
      projectViewLinkSharingData?.sharedState as Exclude<WebExportSharedState, "not_shared">
    );
  }, [projectViewLinkSharingData?.sharedState, setValue]);

  useEffect(() => {
    setValue("baseRole", projectViewLinkSharingData?.baseRole as WebExportBaseRole);
  }, [projectViewLinkSharingData?.baseRole, setValue]);

  const resetForm = useCallback(() => {
    reset(
      {
        generalAccess: projectViewLinkSharingData?.sharedState as Exclude<
          WebExportSharedState,
          "not_shared"
        >,
        baseRole: projectViewLinkSharingData?.baseRole as WebExportBaseRole,
      },
      { keepErrors: true }
    );
  }, [reset, projectViewLinkSharingData]);

  const onClose = useCallback(() => {
    tracker.trackShareModalVisibilityChanged({
      projectId,
      sharedResource: "project",
      visible: false,
    });
    consumeShareIntent();
    resetForm();
    clearErrors();
    setShareModalOpen(false);
  }, [resetForm, clearErrors, consumeShareIntent, tracker, projectId]);

  const { data: projectSharingData } = useProjectSharing();
  const { data: accountData } = useAccount();

  const spaceAccessLabel = useMemo(() => {
    if (projectSharingData?.space.spaceType === "private") {
      return "Only people you directly invite";
    }

    const spaceName = projectSharingData?.space.spaceName;
    return `Anyone in ${spaceName ? `"${spaceName}"` : "this space"}`;
  }, [projectSharingData?.space.spaceName, projectSharingData?.space.spaceType]);

  const spaceAccessDescription = useMemo(() => {
    if (projectSharingData?.space.spaceType === "private") {
      return "";
    }

    const userCount = projectSharingData?.space?.spaceUsers?.length ?? 0;
    const isPlural = userCount !== 1;
    const personNoun = isPlural ? "people" : "person";
    const verb = isPlural ? "have" : "has";
    const memberNoun = isPlural ? "members" : "a member";

    return `${userCount} ${personNoun} ${verb} access to this project because they're ${memberNoun} of the space where this project lives.`;
  }, [projectSharingData?.space.spaceType, projectSharingData?.space?.spaceUsers?.length]);

  return (
    <>
      <Button
        onClick={() => {
          setShareModalOpen(true);
          tracker.trackShareModalVisibilityChanged({
            projectId,
            sharedResource: "project",
            visible: true,
          });
        }}
      >
        Share
      </Button>
      <Modal
        className={style.modal}
        open={shareModalOpen}
        onClose={onClose}
        title="Share this project"
      >
        <OverlayProvider>
          <div className={style.container}>
            <div>
              <span className={style.sectionTitle}>People with access</span>
              <EmailInvitationFormProject />
            </div>

            <div className={style.accessSection}>
              <div className={style.iconWrapper}>
                {projectSharingData?.space.spaceType === "private" ? (
                  <Icon_People_Slash />
                ) : (
                  <Icon_People />
                )}
              </div>
              <div style={{ width: "calc(80% - 30px)" }}>
                <div style={{ fontWeight: "bold" }}>{spaceAccessLabel}</div>
                <div style={{ fontSize: "12px", color: "#6c757d" }}>{spaceAccessDescription}</div>
              </div>
              <div style={{ width: "100px", textAlign: "center" }}>
                <div style={{ fontWeight: "bold" }}>Can Access</div>
              </div>
            </div>

            <div className={style.emailListContainer}>
              {accountData && (
                <div className={style.spaceContainer}>
                  <Avatar title={accountData.name ?? accountData.email} />
                  <span>
                    <b>{`${accountData.name ?? accountData.email} (You)`}</b>
                  </span>
                  <span>Editor</span>
                </div>
              )}

              {projectSharingData?.users
                .filter((user) => !user.invitationId)
                .map((user) => (
                  <Fragment key={user.userId}>
                    <Separator />
                    <MemberRowProject
                      member={
                        {
                          email: user.email,
                          baseRole: user.baseRole,
                          userId: user.userId,
                          name: user.name,
                        } as ProjectViewLinkMember
                      }
                    />
                  </Fragment>
                ))}
              {projectSharingData?.users
                .filter((user) => user.invitationId !== undefined)
                .map((invitation) => (
                  <Fragment key={invitation.invitationId}>
                    <Separator />
                    <InvitationRowProject invitation={invitation as ProjectViewLinkInvitation} />
                  </Fragment>
                ))}

              <Separator className={classNames(style.overflowSeparator, style.bottom)} />
            </div>

            <div className={style.footerContainer}>
              <div className={style.copyLinkContainer}>
                <Button
                  variant="grayscale"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${location.pathname}`);
                    setCopySuccessVisible(true);
                    setTimeout(() => {
                      setCopySuccessVisible(false);
                    }, 1000);
                  }}
                >
                  <ButtonIconWithLabel icon={<Icon_Disconnect24 />} label="Copy link" />
                </Button>
                <div
                  className={classNames(
                    style.copySuccessContainer,
                    copySuccessVisible && style.visible
                  )}
                >
                  <Icon_Checkmark24 />
                  <span>Copied!</span>
                </div>
              </div>
            </div>
          </div>
        </OverlayProvider>
      </Modal>
    </>
  );
};
